import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  Home,
  Users,
  Calendar,
  Clock,
  Activity,
  Settings,
  UserPlus,
  MessageCircle,
  UserCheck,
  UsersRound,
  ShieldCheck,
  ScrollText,
  FilePen,
  Building2,
  Mail
} from 'lucide-react';

const NavigationContext = createContext();

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export const NavigationProvider = ({ children }) => {
  const [userRole, setUserRole] = useState(null);

  // Function to update role from localStorage
  const updateRoleFromStorage = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      console.log('NavigationContext: Current user from localStorage:', user);
      if (user && user.role) {
        console.log('NavigationContext: Setting user role to:', user.role);
        setUserRole(user.role);
      } else {
        console.log('NavigationContext: No user role found in localStorage');
        setUserRole(null);
      }
    } catch (error) {
      console.error('NavigationContext: Error parsing user from localStorage:', error);
      setUserRole(null);
    }
  };

  useEffect(() => {
    // Initial role check
    updateRoleFromStorage();

    // Add storage event listener
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        console.log('NavigationContext: User data changed in localStorage');
        updateRoleFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const canAccessOnboarding = (role) => {
    const hasAccess = ['admin', 'operations', 'super_admin'].includes(role);
    console.log('NavigationContext: Checking onboarding access for role:', role, 'Result:', hasAccess);
    return hasAccess;
  };

  const canAccessUserManagement = (role) => {
    const hasAccess = ['admin', 'super_admin'].includes(role);
    console.log('NavigationContext: Checking user management access for role:', role, 'Result:', hasAccess);
    return hasAccess;
  };

  const canAccessOrgManagement = (role) => {
    return role === 'super_admin';
  };

  const getMenuItems = (role) => {
    console.log('NavigationContext: Getting menu items for role:', role);
    const baseItems = [
      {
        name: 'Painel',
        icon: Home,
        href: '/dashboard'
      },
      {
        name: 'Pacientes',
        icon: Users,
        href: '/dashboard/patients'
      },
      {
        name: 'Sessões',
        icon: Clock,
        href: '/dashboard/sessions'
      },
      {
        name: 'Análises',
        icon: Activity,
        href: '/dashboard/analytics'
      }
    ];

    // Add user management menu item only for admin and supervisor roles
    if (canAccessUserManagement(role)) {
      console.log('NavigationContext: Adding user management menu item for role:', role);
      baseItems.push({
        name: 'Atribuir Pacientes',
        icon: UsersRound,
        href: '/dashboard/user-management'
      });
    }

    // Add organization management for super_admin
    if (canAccessOrgManagement(role)) {
      baseItems.push({
        name: 'Organizações',
        icon: Building2,
        href: '/dashboard/organizations'
      });
    }

    // Outgoing email delivery status, across all organizations — super_admin only
    if (canAccessOrgManagement(role)) {
      baseItems.push({
        name: 'Emails enviados',
        icon: Mail,
        href: '/dashboard/emails'
      });
    }

    // Add parent management menu item only for admin and operations roles
    if (canAccessOnboarding(role)) {
      console.log('NavigationContext: Adding parent management menu item for role:', role);
      baseItems.push({
        name: 'Gerenciar Responsáveis',
        icon: UserCheck,
        href: '/dashboard/parents'
      });
    }

    // Add onboarding menu item only for admin and operations roles
    if (canAccessOnboarding(role)) {
      console.log('NavigationContext: Adding onboarding menu item for role:', role);
      baseItems.push({
        name: 'Gerenciar Usuários',
        icon: UserPlus,
        href: '/dashboard/onboarding'
      });
    }

    // Add GiA chat menu item for all roles
    baseItems.push({
      name: 'GiA chat',
      icon: MessageCircle,
      href: '/dashboard/chat'
    });

    // Audit logs sit after GiA chat, with the other admin-only items
    if (canAccessUserManagement(role)) {
      baseItems.push({
        name: 'Logs de Auditoria',
        icon: ScrollText,
        href: '/dashboard/audit-logs'
      });
    }

    // LGPD Correction admin for admin roles
    if (canAccessUserManagement(role)) {
      baseItems.push({
        name: 'Solicitacoes LGPD',
        icon: FilePen,
        href: '/dashboard/privacy/corrections'
      });
    }

    // Privacy center for all authenticated users
    baseItems.push({
      name: 'Privacidade',
      icon: ShieldCheck,
      href: '/dashboard/privacy'
    });

    return baseItems;
  };

  const value = {
    menuItems: getMenuItems(userRole),
    userRole
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationContext; 